/**
 * SickLeaveContext.js
 * Manages sick leave records for staff members
 * Record-only — no entitlement system, no approval workflow
 * Year-scoped running totals that reset automatically each calendar year
 */

import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import SickLeaveService from '../services/SickLeaveService';
import { SickLeaveHelpers } from '../models/SickLeaveModels';
import { useAuth } from './AuthContext';
import { useStaff } from './StaffContext';
import { useDepartment } from './DepartmentContext';

const SickLeaveContext = createContext();

export const useSickLeave = () => {
  const context = useContext(SickLeaveContext);
  if (!context) {
    throw new Error('useSickLeave must be used within SickLeaveProvider');
  }
  return context;
};

export const SickLeaveProvider = ({ children }) => {
  const { currentUser, currentCompany } = useAuth();
  const companyId = currentCompany?.id;
  const { staffMembers, loading: staffLoading } = useStaff();
  const { departments, loading: departmentsLoading } = useDepartment();

  // ========== STATE ==========
  const [sickLeave, setSickLeave] = useState([]);
  const [rawSickLeave, setRawSickLeave] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const activeListenerRef = useRef(null);

  // ========== STABLE REFS ==========
  const staffMembersRef = useRef(staffMembers);
  const departmentsRef = useRef(departments);
  const staffLoadingRef = useRef(staffLoading);
  const departmentsLoadingRef = useRef(departmentsLoading);

  useEffect(() => {
    staffMembersRef.current = staffMembers;
    departmentsRef.current = departments;
    staffLoadingRef.current = staffLoading;
    departmentsLoadingRef.current = departmentsLoading;
  }, [staffMembers, departments, staffLoading, departmentsLoading]);

  // ========== ENRICHMENT ==========

  const enrichSickLeaveStable = useCallback((sickLeaveItem) => {
    if (staffLoadingRef.current || departmentsLoadingRef.current) {
      sickLeaveItem.staffName = 'Lädt...';
      sickLeaveItem.departmentName = 'Lädt...';
      return sickLeaveItem;
    }

    const staff = staffMembersRef.current.find(s => s.id === sickLeaveItem.staffId);
    const department = departmentsRef.current.find(d => d.id === sickLeaveItem.departmentId);

    sickLeaveItem.staffName = staff?.name || 'Unbekannter Mitarbeiter';
    sickLeaveItem.departmentName = department?.name || 'Unbekannte Abteilung';
    return sickLeaveItem;
  }, []);

  const enrichSickLeaveListStable = useCallback((sickLeaveList) => {
    return sickLeaveList.map(enrichSickLeaveStable);
  }, [enrichSickLeaveStable]);

  // ========== LIFECYCLE EFFECTS ==========

  /**
   * Cleanup listener on unmount
   */
  useEffect(() => {
    return () => {
      if (activeListenerRef.current) {
        activeListenerRef.current();
        activeListenerRef.current = null;
      }
    };
  }, []);

  /**
   * Clear error after timeout
   */
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  /**
   * Re-enrich sick leave when staff or department data changes
   */
  useEffect(() => {
    if (rawSickLeave.length > 0 && !staffLoading && !departmentsLoading) {
      console.log('Re-enriching sick leave with updated staff/department data');
      const enriched = rawSickLeave.map(enrichSickLeaveStable);
      setSickLeave(enriched);
    }
  }, [staffMembers, departments, staffLoading, departmentsLoading, rawSickLeave, enrichSickLeaveStable]);

  // ==================== SICK LEAVE METHODS ====================

  /**
   * Add a single sick leave day.
   * Shift gate is enforced in SickLeaveService:
   * - Throws 'Kein geplanter Dienst für diesen Tag' if no shift found
   * - Throws 'Für diesen Tag existiert bereits ein Krankmeldungseintrag' on duplicate
   */
  const addSickLeave = useCallback(async (sickLeaveData) => {
    if (!companyId) throw new Error('No company ID');

    setLoading(true);
    setError(null);

    try {
      const result = await SickLeaveService.addSickLeave(companyId, {
        ...sickLeaveData,
        createdBy: currentUser.uid
      });

      if (!activeListenerRef.current) {
        setRawSickLeave(prev => [...prev, result]);
        const enrichedItem = enrichSickLeaveStable(result);
        setSickLeave(prev => [...prev, enrichedItem]);
      }

      return { success: true, sickLeave: result };
    } catch (err) {
      console.error('Error adding sick leave:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [companyId, currentUser, enrichSickLeaveStable]);

  /**
   * Add sick leave for a date range.
   * Dates with no scheduled shift are skipped silently (business rule).
   * Throws 'Kein geplanter Dienst für den gewählten Zeitraum' if the entire
   * range produces zero valid entries.
   *
   * The real-time listener picks up new records automatically when active,
   * so no manual state update is needed here.
   *
   * @returns {{ success: true, added: number, skipped: number }}
   */
  const addSickLeaveRange = useCallback(async (sickLeaveData) => {
    if (!companyId) throw new Error('No company ID');

    setLoading(true);
    setError(null);

    try {
      const result = await SickLeaveService.addSickLeaveRange(companyId, {
        ...sickLeaveData,
        createdBy: currentUser.uid
      });

      return { success: true, ...result };
    } catch (err) {
      console.error('Error adding sick leave range:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [companyId, currentUser]);

  /**
   * Delete a sick leave record
   */
  const deleteSickLeave = useCallback(async (sickLeaveId) => {
    if (!companyId) throw new Error('No company ID');

    setLoading(true);
    setError(null);

    try {
      await SickLeaveService.deleteSickLeave(companyId, sickLeaveId);

      if (!activeListenerRef.current) {
        setRawSickLeave(prev => prev.filter(s => s.id !== sickLeaveId));
        setSickLeave(prev => prev.filter(s => s.id !== sickLeaveId));
      }

      return { success: true };
    } catch (err) {
      console.error('Error deleting sick leave:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  /**
   * Fetch sick leave for a department in a date range
   * Sets global sickLeave state
   */
  const fetchSickLeaveByDepartment = useCallback(async (departmentId, startDate, endDate) => {
    if (!companyId) throw new Error('No company ID');

    setLoading(true);
    setError(null);

    try {
      const result = await SickLeaveService.getSickLeaveByDepartment(
        companyId,
        departmentId,
        startDate,
        endDate
      );

      setRawSickLeave(result);
      const enrichedResult = enrichSickLeaveListStable(result);
      setSickLeave(enrichedResult);
      return enrichedResult;
    } catch (err) {
      console.error('Error fetching sick leave by department:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [companyId, enrichSickLeaveListStable]);

  /**
   * Fetch sick leave for a staff member in a date range
   * Sets global sickLeave state
   */
  const fetchSickLeaveByStaff = useCallback(async (staffId, startDate, endDate) => {
    if (!companyId) throw new Error('No company ID');

    setLoading(true);
    setError(null);

    try {
      const result = await SickLeaveService.getSickLeaveByStaff(
        companyId,
        staffId,
        startDate,
        endDate
      );

      setRawSickLeave(result);
      const enrichedResult = enrichSickLeaveListStable(result);
      setSickLeave(enrichedResult);
      return enrichedResult;
    } catch (err) {
      console.error('Error fetching sick leave by staff:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [companyId, enrichSickLeaveListStable]);

  /**
   * Fetch sick leave for a staff member for a full calendar year
   * Totals naturally reset each calendar year — no extra logic required
   * Delegates to fetchSickLeaveByStaff with year-scoped date range
   */
  const fetchSickLeaveByStaffAndYear = useCallback(async (staffId, year) => {
    if (!companyId) throw new Error('No company ID');

    const startDate = new Date(year, 0, 1);
    startDate.setHours(0, 0, 0, 0);

    const endDate = new Date(year, 11, 31);
    endDate.setHours(23, 59, 59, 999);

    return await fetchSickLeaveByStaff(staffId, startDate, endDate);
  }, [companyId, fetchSickLeaveByStaff]);

  /**
   * Fetch sick leave for a date range, optionally filtered by department IDs
   * Sets global sickLeave state
   */
  const fetchSickLeaveByDateRange = useCallback(async (startDate, endDate, departmentIds = null) => {
    if (!companyId) throw new Error('No company ID');

    setLoading(true);
    setError(null);

    try {
      let result;
      if (departmentIds && departmentIds.length > 0) {
        const allSickLeave = [];
        for (const deptId of departmentIds) {
          const deptSickLeave = await SickLeaveService.getSickLeaveByDepartment(
            companyId,
            deptId,
            startDate,
            endDate
          );
          allSickLeave.push(...deptSickLeave);
        }
        result = allSickLeave;
      } else {
        result = await SickLeaveService.getSickLeaveByDateRange(
          companyId,
          startDate,
          endDate
        );
      }

      setRawSickLeave(result);
      const enrichedResult = enrichSickLeaveListStable(result);
      setSickLeave(enrichedResult);
      return enrichedResult;
    } catch (err) {
      console.error('Error fetching sick leave by date range:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [companyId, enrichSickLeaveListStable]);

  /**
   * Fetch sick leave for a specific day
   * Does NOT set global state — returns enriched array directly
   */
  const fetchSickLeaveForDay = useCallback(async (date, departmentIds = null) => {
    if (!companyId) throw new Error('No company ID');

    try {
      const result = await SickLeaveService.getSickLeaveForDay(
        companyId,
        date,
        departmentIds
      );

      return enrichSickLeaveListStable(result);
    } catch (err) {
      console.error('Error fetching sick leave for day:', err);
      throw err;
    }
  }, [companyId, enrichSickLeaveListStable]);

  /**
   * Get sick leave summary statistics for a full calendar year
   * No entitlement or remaining-days fields — record-only totals
   * Returns: { totalDays, totalMinutes, totalHours, formattedTotalDuration, topStaff[], byStaff[] }
   */
  const getYearSummary = useCallback(async (year, departmentIds = null) => {
    if (!companyId) throw new Error('No company ID');

    try {
      const staffMap = {};
      staffMembersRef.current.forEach(staff => {
        staffMap[staff.id] = staff;
      });

      const summary = await SickLeaveService.getYearSummary(
        companyId,
        year,
        departmentIds,
        staffMap
      );

      const enrichedTopStaff = summary.getTopStaff().map(staffStat => {
        const staff = staffMembersRef.current.find(s => s.id === staffStat.staffId);
        return {
          ...staffStat,
          name: staff?.name || staffStat.name || 'Unbekannter Mitarbeiter',
          formattedDuration: SickLeaveHelpers.formatDurationFromMinutes(staffStat.minutes)
        };
      });

      return {
        totalDays: summary.totalDays,
        totalMinutes: summary.totalMinutes,
        totalHours: summary.getTotalHours(),
        formattedTotalDuration: summary.getFormattedTotalDuration(),
        topStaff: enrichedTopStaff,
        byStaff: Array.from(summary.byStaff.values()).map(staffStat => {
          const staff = staffMembersRef.current.find(s => s.id === staffStat.staffId);
          return {
            ...staffStat,
            name: staff?.name || staffStat.name || 'Unbekannter Mitarbeiter',
            formattedDuration: SickLeaveHelpers.formatDurationFromMinutes(staffStat.minutes)
          };
        })
      };
    } catch (err) {
      console.error('Error getting year summary:', err);
      throw err;
    }
  }, [companyId]);

  /**
   * Subscribe to sick leave changes in a date range (real-time).
   * Queries by departmentId field on the sick leave record.
   * Use subscribeToSickLeaveByStaffIds when filtering by department membership
   * or individual staff member — that method queries by staffId instead.
   *
   * Cleans up any previous listener before setting up the new one.
   * Sets loading = true until the first callback fires.
   */
  const subscribeToSickLeave = useCallback((startDate, endDate, departmentIds = null) => {
    if (!companyId) return;

    console.log('Setting up sick leave subscription');

    if (activeListenerRef.current) {
      console.log('Cleaning up previous listener');
      activeListenerRef.current();
      activeListenerRef.current = null;
    }

    setLoading(true);

    const unsubscribeFn = SickLeaveService.subscribeToSickLeave(
      companyId,
      startDate,
      endDate,
      (updatedSickLeave) => {
        console.log('Received sick leave update:', updatedSickLeave.length, 'items');
        setRawSickLeave(updatedSickLeave);
        const enriched = updatedSickLeave.map(s => {
          const staff = staffMembersRef.current.find(m => m.id === s.staffId);
          const department = departmentsRef.current.find(d => d.id === s.departmentId);
          s.staffName = staff?.name || 'Unbekannter Mitarbeiter';
          s.departmentName = department?.name || 'Unbekannte Abteilung';
          return s;
        });
        setSickLeave(enriched);
        setLoading(false);
      },
      departmentIds
    );

    activeListenerRef.current = unsubscribeFn;

    return unsubscribeFn;
  }, [companyId]);

  /**
   * Subscribe to sick leave for a specific set of staff members in a date range.
   *
   * Used when filtering by department or by individual staff member on the page.
   * Queries by staffId instead of departmentId so that sick leave recorded against
   * any department is correctly captured.
   *
   * If staffIds is empty, clears state immediately and returns a no-op.
   * Cleans up any previous listener before setting up the new one.
   * Sets loading = true until the first callback fires.
   */
  const subscribeToSickLeaveByStaffIds = useCallback((startDate, endDate, staffIds) => {
    if (!companyId) return;

    if (activeListenerRef.current) {
      activeListenerRef.current();
      activeListenerRef.current = null;
    }

    if (!staffIds || staffIds.length === 0) {
      setSickLeave([]);
      setRawSickLeave([]);
      setLoading(false);
      return () => {};
    }

    setLoading(true);

    const unsubscribeFn = SickLeaveService.subscribeToSickLeaveByStaffIds(
      companyId,
      startDate,
      endDate,
      staffIds,
      (updatedSickLeave) => {
        setRawSickLeave(updatedSickLeave);
        const enriched = updatedSickLeave.map(s => {
          const staff = staffMembersRef.current.find(m => m.id === s.staffId);
          const department = departmentsRef.current.find(d => d.id === s.departmentId);
          s.staffName = staff?.name || 'Unbekannter Mitarbeiter';
          s.departmentName = department?.name || 'Unbekannte Abteilung';
          return s;
        });
        setSickLeave(enriched);
        setLoading(false);
      }
    );

    activeListenerRef.current = unsubscribeFn;

    return unsubscribeFn;
  }, [companyId]);

  /**
   * Subscribe to sick leave for a specific day (real-time)
   */
  const subscribeToSickLeaveForDay = useCallback((date, departmentIds = null) => {
    if (!companyId) return;

    if (activeListenerRef.current) {
      activeListenerRef.current();
      activeListenerRef.current = null;
    }

    const unsubscribeFn = SickLeaveService.subscribeToSickLeaveForDay(
      companyId,
      date,
      (updatedSickLeave) => {
        setRawSickLeave(updatedSickLeave);
        const enriched = updatedSickLeave.map(s => {
          const staff = staffMembersRef.current.find(m => m.id === s.staffId);
          const department = departmentsRef.current.find(d => d.id === s.departmentId);
          s.staffName = staff?.name || 'Unbekannter Mitarbeiter';
          s.departmentName = department?.name || 'Unbekannte Abteilung';
          return s;
        });
        setSickLeave(enriched);
      },
      departmentIds
    );

    activeListenerRef.current = unsubscribeFn;

    return unsubscribeFn;
  }, [companyId]);

  /**
   * Unsubscribe from the active real-time listener
   */
  const unsubscribe = useCallback(() => {
    if (activeListenerRef.current) {
      console.log('Unsubscribing from sick leave listener');
      activeListenerRef.current();
      activeListenerRef.current = null;
    }
  }, []);

  // ==================== UTILITY CALLBACKS ====================

  const groupSickLeaveByDate = useCallback(() => {
    return SickLeaveService.groupSickLeaveByDate(sickLeave);
  }, [sickLeave]);

  const groupSickLeaveByStaff = useCallback(() => {
    return SickLeaveService.groupSickLeaveByStaff(sickLeave);
  }, [sickLeave]);

  const getSickLeaveForDate = useCallback((date) => {
    const dateStr = date instanceof Date
      ? date.toISOString().split('T')[0]
      : date;

    return sickLeave.filter(s => s.getDateString() === dateStr);
  }, [sickLeave]);

  const getSickLeaveForStaff = useCallback((staffId) => {
    return sickLeave.filter(s => s.staffId === staffId);
  }, [sickLeave]);

  const hasSickLeaveOnDate = useCallback((staffId, date) => {
    const dateStr = date instanceof Date
      ? date.toISOString().split('T')[0]
      : date;

    return sickLeave.some(
      s => s.staffId === staffId && s.getDateString() === dateStr
    );
  }, [sickLeave]);

  const getSickLeaveCountForDate = useCallback((date) => {
    return getSickLeaveForDate(date).length;
  }, [getSickLeaveForDate]);

  const getTotalMinutesForDate = useCallback((date) => {
    const daySickLeave = getSickLeaveForDate(date);
    return daySickLeave.reduce((sum, s) => sum + (s.durationMinutes || 0), 0);
  }, [getSickLeaveForDate]);

  const getTotalHoursForDate = useCallback((date) => {
    const totalMinutes = getTotalMinutesForDate(date);
    return totalMinutes / 60;
  }, [getTotalMinutesForDate]);

  const getFormattedDurationForDate = useCallback((date) => {
    const totalMinutes = getTotalMinutesForDate(date);
    return SickLeaveHelpers.formatDurationFromMinutes(totalMinutes);
  }, [getTotalMinutesForDate]);

  const clearSickLeave = useCallback(() => {
    setSickLeave([]);
    setRawSickLeave([]);
  }, []);

  // ==================== CONTEXT VALUE ====================

  const value = {
    // State
    sickLeave,
    loading,
    error,

    // CRUD operations
    addSickLeave,
    addSickLeaveRange,
    deleteSickLeave,

    // Fetch operations
    fetchSickLeaveByDepartment,
    fetchSickLeaveByStaff,
    fetchSickLeaveByStaffAndYear,
    fetchSickLeaveByDateRange,
    fetchSickLeaveForDay,

    // Summary
    getYearSummary,

    // Real-time subscriptions
    subscribeToSickLeave,
    subscribeToSickLeaveByStaffIds,
    subscribeToSickLeaveForDay,
    unsubscribe,

    // Utility functions
    groupSickLeaveByDate,
    groupSickLeaveByStaff,
    getSickLeaveForDate,
    getSickLeaveForStaff,
    hasSickLeaveOnDate,
    getSickLeaveCountForDate,
    getTotalMinutesForDate,
    getTotalHoursForDate,
    getFormattedDurationForDate,
    clearSickLeave,
    enrichSickLeave: enrichSickLeaveStable
  };

  return (
    <SickLeaveContext.Provider value={value}>
      {children}
    </SickLeaveContext.Provider>
  );
};

export default SickLeaveContext;